/**
 * AdminHome — /#/admin
 *
 * User mgmt, quota assignment, tenant settings.
 * Roles: Manager, Executive
 *
 * Tabs: Users / Quotas / Roles / Tenant
 * Users + Quotas pulled live from Supabase (no seed data — production launch May 2026).
 */

import { useEffect, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { getSession, supabase } from "@/lib/session";
import { friendlyError } from "@/lib/friendly-error";
import { TenantAdequacyToggle } from "@/components/admin/TenantAdequacyToggle";
import { ManagerAdequacyQueue } from "@/components/admin/ManagerAdequacyQueue";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const BG = "#FAFAF7";

// ── Types ─────────────────────────────────────────────────────────────────────

type Role = "bdr" | "ae" | "manager" | "executive" | "marketing" | "se";

interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  active: boolean;
  quotaAnnual: number;
  isDualRole: boolean;
}

interface Quota {
  userId: string;
  name: string;
  role: Role;
  annual: number;
}

const ALL_ROLES: Role[] = ["bdr", "ae", "manager", "executive", "marketing", "se"];

interface RoleDef {
  role: Role;
  label: string;
  description: string;
  perms: string[];
}

const ROLE_DEFS: RoleDef[] = [
  {
    role: "bdr",
    label: "BDR",
    description: "Business Development — cold outreach, CP1 qualification, CP1 graduation.",
    perms: ["View accounts", "Create contacts", "Log activity", "View cadence", "Holding queue access"],
  },
  {
    role: "ae",
    label: "AE",
    description: "Account Executive — owns the full sales cycle from CP2 through close.",
    perms: ["View accounts", "Create/edit accounts", "Log activity", "Manage pipeline", "View leaderboard", "Plan creation"],
  },
  {
    role: "manager",
    label: "Manager",
    description: "Sales Manager — plan approval, quota assignment, team coaching.",
    perms: ["All AE perms", "Approve plans", "Assign quotas", "Team reporting", "Forecast view", "Admin access", "Override policy"],
  },
  {
    role: "executive",
    label: "Executive",
    description: "Executive — board-level reporting, org-wide visibility, tenant settings.",
    perms: ["All Manager perms", "Board reports", "Tenant settings", "Helix score view", "Full audit log"],
  },
  {
    role: "marketing",
    label: "Marketing",
    description: "Marketing — campaign planning, hooks library, event management, MQL tracking.",
    perms: ["View accounts", "Marketing plan", "Hooks library", "Calendar management", "Activity log"],
  },
  {
    role: "se",
    label: "SE",
    description: "Solutions Engineer — technical discovery support, Helix score qualification.",
    perms: ["View accounts", "Helix demo", "Question bank", "Templates", "Sales path"],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtUSD(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

// ── UsersTab (full user management) ────────────────────────────────────────────────────────
function UsersTab({
  users, loading, error, currentUserId, tenantId, onChange,
}: {
  users: User[];
  loading: boolean;
  error: string;
  currentUserId: string;
  tenantId: number;
  onChange: (updater: (prev: User[]) => User[]) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<Role>("ae");
  const [editName, setEditName] = useState("");
  const [editQuota, setEditQuota] = useState<string>("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [rowError, setRowError] = useState<string>("");

  function startEdit(u: User) {
    setEditingId(u.id);
    setEditRole(u.role);
    setEditName(u.name);
    setEditQuota(String(u.quotaAnnual || 0));
    setRowError("");
  }

  async function saveEdit(u: User) {
    setBusyId(u.id);
    setRowError("");
    try {
      const sb = supabase();
      const quotaNum = Number(editQuota.replace(/[^0-9.]/g, "")) || 0;
      const { error: upErr } = await sb
        .from("users")
        .update({ role: editRole, full_name: editName.trim() || u.email, quota_annual: quotaNum })
        .eq("id", u.id);
      if (upErr) throw new Error(upErr.message);
      onChange((prev) => prev.map((x) => x.id === u.id ? { ...x, role: editRole, name: editName.trim() || u.email, quotaAnnual: quotaNum } : x));
      setEditingId(null);
    } catch (err: any) {
      setRowError(err?.message || "Could not save changes.");
    } finally {
      setBusyId(null);
    }
  }

  async function toggleActive(u: User) {
    if (u.id === currentUserId) {
      setRowError("You cannot deactivate your own account.");
      return;
    }
    const next = !u.active;
    const verb = next ? "reactivate" : "deactivate";
    if (!window.confirm(`${next ? "Reactivate" : "Deactivate"} ${u.name}? They will ${next ? "regain" : "lose"} access on next sign-in.`)) return;
    setBusyId(u.id);
    setRowError("");
    try {
      const sb = supabase();
      const { error: upErr } = await sb.from("users").update({ is_active: next }).eq("id", u.id);
      if (upErr) throw new Error(upErr.message);
      onChange((prev) => prev.map((x) => x.id === u.id ? { ...x, active: next } : x));
    } catch (err: any) {
      setRowError(err?.message || `Could not ${verb} user.`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#6B6B70" }}>
          {loading ? "Loading…" : `${users.length} ${users.length === 1 ? "user" : "users"} in this tenant`}
        </div>
        <button
          onClick={() => setShowInvite(true)}
          style={{
            padding: "8px 14px", borderRadius: 8, background: BLACK, color: "#FFFFFF",
            fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
            border: "none", cursor: "pointer",
          }}
        >
          + Add user
        </button>
      </div>
      {rowError && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C", padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12 }}>
          {rowError}
        </div>
      )}
      <div style={{ background: "#FFFFFF", border: "1px solid #E5E5E7", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #E5E5E7", background: "#FAFAF7", display: "grid", gridTemplateColumns: "2fr 2fr 110px 110px 90px 180px", gap: 12 }}>
          {["Name", "Email", "Role", "Annual quota", "Status", ""].map((h) => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: "#8E8E94" }}>{h}</div>
          ))}
        </div>
        {loading ? (
          <div style={{ padding: "24px 20px", fontSize: 12, color: "#8E8E94" }}>Loading users…</div>
        ) : error ? (
          <div style={{ padding: "24px 20px", fontSize: 12, color: "#B91C1C" }}>Could not load users: {error}</div>
        ) : users.length === 0 ? (
          <div style={{ padding: "24px 20px", fontSize: 12, color: "#8E8E94" }}>No users in this tenant yet.</div>
        ) : users.map((u) => {
          const isEditing = editingId === u.id;
          const isBusy = busyId === u.id;
          return (
            <div
              key={u.id}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 2fr 110px 110px 90px 180px",
                gap: 12,
                padding: "12px 20px",
                borderBottom: "1px solid #EFEFEE",
                alignItems: "center",
                opacity: u.active ? 1 : 0.55,
              }}
            >
              {isEditing ? (
                <input value={editName} onChange={(e) => setEditName(e.target.value)} style={editInputStyle} />
              ) : (
                <div style={{ fontSize: 13, fontWeight: 600, color: BLACK }}>{u.name}</div>
              )}
              <div style={{ fontSize: 12, color: "#6B6B70" }}>{u.email}</div>
              {isEditing ? (
                <select value={editRole} onChange={(e) => setEditRole(e.target.value as Role)} style={editInputStyle}>
                  {ALL_ROLES.map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
                </select>
              ) : (
                <RoleBadge role={u.role} />
              )}
              {isEditing ? (
                <input value={editQuota} onChange={(e) => setEditQuota(e.target.value)} placeholder="0" style={{ ...editInputStyle, fontVariantNumeric: "tabular-nums" }} />
              ) : (
                <div style={{ fontSize: 12, color: "#6B6B70", fontVariantNumeric: "tabular-nums" }}>
                  {u.quotaAnnual > 0 ? fmtUSD(u.quotaAnnual) : "—"}
                </div>
              )}
              <div>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                  padding: "2px 8px", borderRadius: 99,
                  background: u.active ? "#F0FDF4" : "#FAFAF7",
                  color: u.active ? "#15803D" : "#8E8E94",
                  border: u.active ? "1px solid #BBF7D0" : "1px solid #E5E5E7",
                }}>
                  {u.active ? "Active" : "Inactive"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                {isEditing ? (
                  <>
                    <button onClick={() => saveEdit(u)} disabled={isBusy} style={primaryBtn}>
                      {isBusy ? "Saving…" : "Save"}
                    </button>
                    <button onClick={() => setEditingId(null)} disabled={isBusy} style={ghostBtn}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(u)} disabled={isBusy} style={ghostBtn}>Edit</button>
                    <button
                      onClick={() => toggleActive(u)}
                      disabled={isBusy || u.id === currentUserId}
                      title={u.id === currentUserId ? "You cannot deactivate your own account" : undefined}
                      style={u.active ? dangerBtn : primaryBtn}
                    >
                      {isBusy ? "Working…" : (u.active ? "Deactivate" : "Reactivate")}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {showInvite && (
        <InviteUserModal
          tenantId={tenantId}
          onClose={() => setShowInvite(false)}
          onCreated={(u) => {
            onChange((prev) => [...prev, u].sort((a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name)));
            setShowInvite(false);
          }}
        />
      )}
    </div>
  );
}

const editInputStyle: React.CSSProperties = {
  width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #C9C9CC",
  fontSize: 12, background: "#FFFFFF", color: "#0B0B0F",
};
const primaryBtn: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
  padding: "5px 10px", borderRadius: 6, background: BLACK, color: "#FFFFFF",
  border: "none", cursor: "pointer",
};
const ghostBtn: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
  padding: "4px 10px", borderRadius: 6, background: "transparent",
  border: "1px solid #C9C9CC", color: "#525258", cursor: "pointer",
};
const dangerBtn: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
  padding: "4px 10px", borderRadius: 6, background: "transparent",
  border: "1px solid #FCA5A5", color: "#B91C1C", cursor: "pointer",
};

function InviteUserModal({
  tenantId, onClose, onCreated,
}: {
  tenantId: number;
  onClose: () => void;
  onCreated: (u: User) => void;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<Role>("ae");
  const [quota, setQuota] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setOk("");
    try {
      const cleanEmail = email.trim().toLowerCase();
      if (!cleanEmail || !cleanEmail.includes("@")) throw new Error("Enter a valid email.");
      const sb = supabase();
      // Generate a placeholder uuid; the user will need a separate Supabase Auth account.
      // For now this row gives them a slot. Admin must run the auth invite separately.
      const newId = (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random()}`;
      const quotaNum = Number(quota.replace(/[^0-9.]/g, "")) || 0;
      const { data, error: insErr } = await sb
        .from("users")
        .insert({
          id: newId,
          tenant_id: tenantId,
          email: cleanEmail,
          full_name: fullName.trim() || cleanEmail,
          role,
          quota_annual: quotaNum,
          must_change_password: true,
          is_active: true,
        })
        .select("id, email, full_name, role, quota_annual, is_active, is_dual_role")
        .single();
      if (insErr) throw new Error(insErr.message);
      const r = data as any;
      onCreated({
        id: r.id, email: r.email, name: r.full_name || r.email, role: r.role,
        active: r.is_active !== false, quotaAnnual: Number(r.quota_annual || 0), isDualRole: !!r.is_dual_role,
      });
      setOk("User row created. Send them a sign-in link or reset email next.");
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(11,11,15,0.45)", zIndex: 10000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#FFFFFF", borderRadius: 12, padding: 24, maxWidth: 480, width: "100%",
        boxShadow: "0 24px 60px rgba(11,11,15,0.25)",
      }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: BLACK, marginBottom: 6 }}>Add user</div>
        <div style={{ fontSize: 12, color: "#6B6B70", marginBottom: 16 }}>
          Creates the users row in this tenant. After creating, send the new user a password reset link from the Login page so they can set their password.
        </div>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Email">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" style={modalInput} />
          </Field>
          <Field label="Full name">
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" style={modalInput} />
          </Field>
          <Field label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value as Role)} style={modalInput}>
              {ALL_ROLES.map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
            </select>
          </Field>
          <Field label="Annual quota (USD)">
            <input value={quota} onChange={(e) => setQuota(e.target.value)} placeholder="0" style={modalInput} />
          </Field>
          {error && <div style={{ fontSize: 12, color: "#B91C1C", background: "#FEF2F2", border: "1px solid #FECACA", padding: "8px 12px", borderRadius: 6 }}>{error}</div>}
          {ok && <div style={{ fontSize: 12, color: "#15803D", background: "#F0FDF4", border: "1px solid #BBF7D0", padding: "8px 12px", borderRadius: 6 }}>{ok}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button type="submit" disabled={busy} style={{ ...primaryBtn, padding: "9px 16px", fontSize: 12 }}>
              {busy ? "Creating…" : "Create user"}
            </button>
            <button type="button" onClick={onClose} disabled={busy} style={{ ...ghostBtn, padding: "8px 16px", fontSize: 12 }}>
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: "#6B6B70" }}>{label}</span>
      {children}
    </label>
  );
}
const modalInput: React.CSSProperties = {
  padding: "8px 10px", borderRadius: 6, border: "1px solid #C9C9CC",
  fontSize: 13, background: "#FFFFFF", color: "#0B0B0F",
};

function RoleBadge({ role }: { role: Role }) {
  const map: Record<Role, { bg: string; color: string }> = {
    bdr:       { bg: "#FAFAF7", color: "#0B0B0F" },
    ae:        { bg: "#F0FDF4", color: "#15803D" },
    manager:   { bg: "#FDF4FF", color: "#0B0B0F" },
    executive: { bg: "#FFF7ED", color: "#C2410C" },
    marketing: { bg: "#FFF1F2", color: "#BE123C" },
    se:        { bg: "#FAFAF7", color: "#2A2A2E" },
  };
  const s = map[role];
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        padding: "2px 8px",
        borderRadius: 99,
      }}
    >
      {role}
    </span>
  );
}

// ── Tab type ──────────────────────────────────────────────────────────────────

type Tab = "users" | "quotas" | "roles" | "tenant";

// ── Main ─────────────────────────────────────────────────────────────────────

export default function AdminHome() {
  const [activeTab, setActiveTab] = useState<Tab>("users");
  const [primaryColor, setPrimaryColor] = useState("#DC2626");
  const [tenantName, setTenantName] = useState("Mi6 Group");
  const session = getSession();

  // Live data from Supabase
  const [users, setUsers] = useState<User[]>([]);
  const [quotas, setQuotas] = useState<Quota[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadError, setLoadError] = useState<string>("");

  useEffect(() => {
    if (!session?.tenantId) {
      setLoadingUsers(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sb = supabase();
        const { data, error } = await sb
          .from("users")
          .select("id, email, full_name, role, quota_annual, is_active, is_dual_role")
          .eq("tenant_id", session.tenantId)
          .order("role", { ascending: true })
          .order("full_name", { ascending: true });
        if (cancelled) return;
        if (error) {
          setLoadError(error.message || "Could not load users.");
          setUsers([]);
          setQuotas([]);
        } else {
          const rows = (data || []) as Array<{
            id: string; email: string; full_name: string | null;
            role: Role; quota_annual: number | null;
            is_active: boolean | null; is_dual_role: boolean | null;
          }>;
          setUsers(
            rows.map((r) => ({
              id: r.id,
              name: r.full_name || r.email,
              email: r.email,
              role: r.role,
              active: r.is_active !== false,
              quotaAnnual: Number(r.quota_annual || 0),
              isDualRole: !!r.is_dual_role,
            })),
          );
          setQuotas(
            rows
              .filter((r) => (r.quota_annual || 0) > 0)
              .map((r) => ({
                userId: r.id,
                name: r.full_name || r.email,
                role: r.role,
                annual: Number(r.quota_annual || 0),
              })),
          );
        }
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(err?.message || "Could not load users.");
        }
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session?.tenantId]);

  const tabs: { id: Tab; label: string }[] = [
    { id: "users",  label: "Users" },
    { id: "quotas", label: "Quotas" },
    { id: "roles",  label: "Roles" },
    { id: "tenant", label: "Tenant" },
  ];

  return (
    <OperatorShell breadcrumbs={[{ label: "Admin" }]}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: BLACK, margin: 0, letterSpacing: "-0.01em" }}>Admin</h1>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #E5E5E7" }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: "8px 16px",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                borderBottom: activeTab === t.id ? `2px solid ${BLACK}` : "2px solid transparent",
                color: activeTab === t.id ? BLACK : "#6B6B70",
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Users tab */}
        {activeTab === "users" && (
          <UsersTab
            users={users}
            loading={loadingUsers}
            error={loadError}
            currentUserId={session?.id || ""}
            tenantId={session?.tenantId || 0}
            onChange={(updater) => setUsers(updater)}
          />
        )}

        {/* Quotas tab */}
        {activeTab === "quotas" && (
          <div style={{ background: "#FFFFFF", border: "1px solid #E5E5E7", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: "12px 20px", borderBottom: "1px solid #E5E5E7", background: "#FAFAF7", display: "grid", gridTemplateColumns: "2fr 80px 120px", gap: 12 }}>
              {["Name", "Role", "Annual quota"].map((h) => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: "#8E8E94" }}>{h}</div>
              ))}
            </div>
            {loadingUsers ? (
              <div style={{ padding: "24px 20px", fontSize: 12, color: "#8E8E94" }}>Loading quotas…</div>
            ) : quotas.length === 0 ? (
              <div style={{ padding: "24px 20px", fontSize: 12, color: "#8E8E94" }}>No quotas assigned in this tenant yet.</div>
            ) : quotas.map((q) => (
              <div
                key={q.userId}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 80px 120px",
                  gap: 12,
                  padding: "14px 20px",
                  borderBottom: "1px solid #EFEFEE",
                  alignItems: "center",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#FAFAF7"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: BLACK }}>{q.name}</div>
                <RoleBadge role={q.role} />
                <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(q.annual)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Roles tab */}
        {activeTab === "roles" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
            {ROLE_DEFS.map((r) => (
              <div key={r.role} style={{ background: "#FFFFFF", border: "1px solid #E5E5E7", borderRadius: 12, padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <RoleBadge role={r.role} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: BLACK }}>{r.label}</span>
                </div>
                <p style={{ fontSize: 12, color: "#6B6B70", margin: "0 0 12px", lineHeight: 1.5 }}>{r.description}</p>
                <div>
                  {r.perms.map((p) => (
                    <div key={p} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <span style={{ color: "#16A34A", fontSize: 12, fontWeight: 700 }}>✓</span>
                      <span style={{ fontSize: 12, color: "#525258" }}>{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tenant tab */}
        {activeTab === "tenant" && (
          <div style={{ maxWidth: 560 }}>
            <div style={{ background: "#FFFFFF", border: "1px solid #E5E5E7", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: BLACK }}>Branding settings</div>

              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B6B70" }}>
                  Tenant name
                </span>
                <input
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  style={{
                    padding: "9px 12px",
                    borderRadius: 8,
                    border: "1px solid #E5E5E7",
                    fontSize: 13,
                    color: BLACK,
                    background: "#FAFAF7",
                  }}
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B6B70" }}>
                  Primary color
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid #E5E5E7", cursor: "pointer", padding: 2 }}
                  />
                  <span style={{ fontSize: 13, color: "#6B6B70", fontFamily: "monospace" }}>{primaryColor.toUpperCase()}</span>
                </div>
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B6B70" }}>
                  Logo
                </span>
                <div
                  style={{
                    border: "1px dashed #C9C9CC",
                    borderRadius: 8,
                    padding: "20px",
                    textAlign: "center",
                    color: "#8E8E94",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Click to upload logo (PNG, SVG) — max 200KB
                </div>
              </label>

              <button
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  background: BLACK,
                  color: "#FFFFFF",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  border: "none",
                  cursor: "pointer",
                  alignSelf: "flex-start",
                }}
              >
                Save settings
              </button>
            </div>

            {/* PUSH Defense Adequacy is a Tenant 1 canon feature; suppressed on other tenants pending rollout per S-CP10-3-A. */}
            {session?.tenantId === 1 ? (
              <div style={{ marginTop: 24, maxWidth: 720 }}>
                <TenantAdequacyToggle
                  supabase={supabase() as any}
                  tenantId={session.tenantId}
                />
                <div style={{ marginTop: 20 }}>
                  <ManagerAdequacyQueue />
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </OperatorShell>
  );
}
